import React, { useState } from 'react';

interface CustomSignUpFormProps {
  onSignUp: (email: string, password: string, familyId: string, familyName?: string) => Promise<void>;
  checkFamilyIdAvailability?: (familyId: string) => Promise<boolean>;
}

// パスワード要件の型定義
interface PasswordRequirements {
  length: boolean;
  uppercase: boolean;
  lowercase: boolean;
  number: boolean;
}

// パスワード強度の型定義
type PasswordStrength = 0 | 1 | 2 | 3 | 4;

export const CustomSignUpForm: React.FC<CustomSignUpFormProps> = ({ 
  onSignUp, 
  checkFamilyIdAvailability 
}) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    familyId: '',
    familyName: ''
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [familyIdChecking, setFamilyIdChecking] = useState(false);
  const [familyIdAvailable, setFamilyIdAvailable] = useState<boolean | null>(null);
  
  // パスワード関連のstate
  const [passwordRequirements, setPasswordRequirements] = useState<PasswordRequirements>({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false
  });
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>(0);
  const [showPassword, setShowPassword] = useState(false);

  const validateEmail = (email: string): string | null => {
    if (!email.trim()) return 'メールアドレスは必須です';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'メールアドレスの形式が正しくありません';
    return null;
  };

  const validateFamilyId = (familyId: string): string | null => {
    if (!familyId.trim()) return 'Family IDは必須です';
    if (familyId.length < 3) return 'Family IDは3文字以上である必要があります';
    if (familyId.length > 20) return 'Family IDは20文字以下である必要があります';
    if (!/^[a-zA-Z0-9_-]+$/.test(familyId)) {
      return 'Family IDは英数字、ハイフン、アンダースコアのみ使用可能です';
    }
    return null;
  };

  // パスワードバリデーション関数
  const validatePassword = (password: string): { requirements: PasswordRequirements; strength: PasswordStrength; error: string | null } => {
    const requirements: PasswordRequirements = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password)
    };
    
    const strength = Object.values(requirements).filter(Boolean).length as PasswordStrength;
    
    let error: string | null = null;
    if (!password) {
      error = 'パスワードは必須です';
    } else if (password.length > 0 && strength < 4) {
      const missing = [];
      if (!requirements.length) missing.push('8文字以上');
      if (!requirements.uppercase) missing.push('大文字');
      if (!requirements.lowercase) missing.push('小文字');
      if (!requirements.number) missing.push('数字');
      error = `パスワードに以下が必要です: ${missing.join('、')}`;
    }
    
    return { requirements, strength, error };
  };

  // 簡素化されたメール変更ハンドラー
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value;
    setFormData(prev => ({ ...prev, email }));

    // バリデーションのみ実行
    const validationError = validateEmail(email);
    setErrors(prev => ({ ...prev, email: validationError || '' }));
  };

  const handleFamilyIdChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const familyId = e.target.value;
    setFormData(prev => ({ ...prev, familyId }));

    // バリデーション
    const validationError = validateFamilyId(familyId);
    if (validationError) {
      setErrors(prev => ({ ...prev, familyId: validationError }));
      setFamilyIdAvailable(null);
      return;
    }

    // checkFamilyIdAvailabilityが提供されていない場合はスキップ
    if (!checkFamilyIdAvailability) {
      setFamilyIdAvailable(null);
      setErrors(prev => ({ ...prev, familyId: '' }));
      return;
    }

    // 可用性チェック
    setFamilyIdChecking(true);
    try {
      const isAvailable = await checkFamilyIdAvailability(familyId);
      setFamilyIdAvailable(isAvailable ?? null);
      if (isAvailable === false) {
        setErrors(prev => ({ ...prev, familyId: 'このFamily IDは既に使用されています' }));
      } else if (isAvailable === true) {
        setErrors(prev => ({ ...prev, familyId: '' }));
      } else {
        setErrors(prev => ({ ...prev, familyId: 'Family IDの確認に失敗しました' }));
      }
    } catch (error) {
      setErrors(prev => ({ ...prev, familyId: 'Family IDの確認に失敗しました' }));
      setFamilyIdAvailable(null);
    } finally {
      setFamilyIdChecking(false);
    }
  };

  // パスワード変更ハンドラー（リアルタイムバリデーション付き）
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const password = e.target.value;
    setFormData(prev => ({ ...prev, password }));
    
    // リアルタイムバリデーション
    const validation = validatePassword(password);
    setPasswordRequirements(validation.requirements);
    setPasswordStrength(validation.strength);
    
    // エラーメッセージ更新
    setErrors(prev => ({ 
      ...prev, 
      password: validation.error || '' 
    }));
    
    // パスワード確認フィールドのバリデーションも更新
    if (formData.confirmPassword && password !== formData.confirmPassword) {
      setErrors(prev => ({ 
        ...prev, 
        confirmPassword: 'パスワードが一致しません' 
      }));
    } else if (formData.confirmPassword && password === formData.confirmPassword) {
      setErrors(prev => ({ 
        ...prev, 
        confirmPassword: '' 
      }));
    }
  };

  // パスワード確認フィールドの変更ハンドラー
  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const confirmPassword = e.target.value;
    setFormData(prev => ({ ...prev, confirmPassword }));
    
    // パスワード一致チェック
    if (confirmPassword && formData.password !== confirmPassword) {
      setErrors(prev => ({ 
        ...prev, 
        confirmPassword: 'パスワードが一致しません' 
      }));
    } else {
      setErrors(prev => ({ 
        ...prev, 
        confirmPassword: '' 
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // バリデーション
    const newErrors: { [key: string]: string } = {};
    
    const emailError = validateEmail(formData.email);
    if (emailError) newErrors.email = emailError;
    
    // パスワードバリデーション
    const passwordValidation = validatePassword(formData.password);
    if (passwordValidation.error) {
      newErrors.password = passwordValidation.error;
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'パスワードが一致しません';
    }
    
    const familyIdError = validateFamilyId(formData.familyId);
    if (familyIdError) newErrors.familyId = familyIdError;
    else if (familyIdAvailable === false) {
      newErrors.familyId = 'このFamily IDは既に使用されています';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    try {
      await onSignUp(formData.email, formData.password, formData.familyId, formData.familyName);
    } catch (error) {
      console.error('Sign up failed:', error);
      // より詳細なエラーメッセージを表示
      if (error instanceof Error) {
        if (error.message.includes('UsernameExistsException') || error.message.includes('User already exists')) {
          setErrors({ email: 'このメールアドレスは既に登録されています。ログインしてください。' });
        } else if (error.message.includes('InvalidPasswordException')) {
          setErrors({ password: 'パスワードの形式が正しくありません。8文字以上で大文字・小文字・数字を含めてください。' });
        } else {
          setErrors({ general: `アカウント作成に失敗しました: ${error.message}` });
        }
      } else {
      setErrors({ general: 'アカウント作成に失敗しました' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-pink-600 mb-2">アカウント作成</h2>
        <p className="text-gray-600">Wedding Task Plannerを始めましょう</p>
        <p className="text-xs text-gray-500 mt-1">メールアドレスがログイン用のユーザー名として使用されます</p>
      </div>

      <div className="bg-pink-50 border border-pink-200 rounded-lg p-3 mb-4">
        <h3 className="text-sm font-semibold text-pink-800 mb-1">📝 アカウント作成について</h3>
        <ul className="text-xs text-pink-700 space-y-1">
          <li>• メールアドレスがログイン用のユーザー名になります</li>
          <li>• Family IDは結婚準備を共有するためのユニークなIDです</li>
          <li>• パートナーと同じファミリーアカウントを使用できます</li>
        </ul>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            メールアドレス（ユーザー名） *
          </label>
          <input
            type="email"
            id="email"
            value={formData.email}
            onChange={handleEmailChange}
            placeholder="例: yourname@example.com"
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 ${
              errors.email ? 'border-red-300' : 'border-gray-300'
            }`}
            disabled={isLoading}
          />
          {errors.email && (
            <p className="text-red-600 text-xs mt-1">{errors.email}</p>
          )}
        </div>

        {/* Family ID */}
        <div>
          <label htmlFor="familyId" className="block text-sm font-medium text-gray-700 mb-1">
            Family ID * <span className="text-xs text-gray-500">(英数字、ハイフン、アンダースコア 3-20文字)</span>
          </label>
          <div className="relative">
            <input
              type="text"
              id="familyId"
              value={formData.familyId}
              onChange={handleFamilyIdChange}
              placeholder="例: tanaka-family, yamada2024"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 ${
                errors.familyId ? 'border-red-300' : familyIdAvailable === true ? 'border-green-300' : 'border-gray-300'
              }`}
              disabled={isLoading}
            />
            {familyIdChecking && (
              <div className="absolute right-3 top-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-pink-600"></div>
              </div>
            )}
            {familyIdAvailable === true && !familyIdChecking && (
              <div className="absolute right-3 top-2 text-green-500">✓</div>
            )}
          </div>
          {errors.familyId && (
            <p className="text-red-600 text-xs mt-1">{errors.familyId}</p>
          )}
          {familyIdAvailable === true && (
            <p className="text-green-600 text-xs mt-1">このFamily IDは利用可能です</p>
          )}
        </div>

        {/* Family Name (Optional) */}
        <div>
          <label htmlFor="familyName" className="block text-sm font-medium text-gray-700 mb-1">
            ファミリーアカウント名 (任意)
          </label>
          <input
            type="text"
            id="familyName"
            value={formData.familyName}
            onChange={(e) => setFormData(prev => ({ ...prev, familyName: e.target.value }))}
            placeholder="例: 田中家、山田ファミリー"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
            disabled={isLoading}
          />
        </div>

        {/* Password */}
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
            パスワード *
          </label>
          <div className="relative">
          <input
              type={showPassword ? 'text' : 'password'}
            id="password"
            value={formData.password}
              onChange={handlePasswordChange}
              className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 ${
                errors.password ? 'border-red-300' : 
                passwordStrength === 4 ? 'border-green-300' : 'border-gray-300'
            }`}
            disabled={isLoading}
              placeholder="パスワードを入力してください"
          />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-2 text-gray-400 hover:text-gray-600 focus:outline-none"
              disabled={isLoading}
            >
              {showPassword ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>
          
          {/* パスワード強度インジケーター */}
          {formData.password && (
            <div className="mt-2">
              <div className="flex items-center space-x-2">
                <div className="flex-1 bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${
                      passwordStrength === 1 ? 'w-1/4 bg-red-500' :
                      passwordStrength === 2 ? 'w-2/4 bg-yellow-500' :
                      passwordStrength === 3 ? 'w-3/4 bg-blue-500' :
                      passwordStrength === 4 ? 'w-full bg-green-500' : 'w-0'
                    }`}
                  />
                </div>
                <span className={`text-xs font-medium ${
                  passwordStrength === 1 ? 'text-red-600' :
                  passwordStrength === 2 ? 'text-yellow-600' :
                  passwordStrength === 3 ? 'text-blue-600' :
                  passwordStrength === 4 ? 'text-green-600' : 'text-gray-400'
                }`}>
                  {passwordStrength === 0 ? '' :
                   passwordStrength === 1 ? '弱い' :
                   passwordStrength === 2 ? '普通' :
                   passwordStrength === 3 ? '良い' : '強い'}
                </span>
              </div>
            </div>
          )}

          {/* パスワード要件チェックリスト */}
          {formData.password && (
            <div className="mt-2 space-y-1">
              {[
                { key: 'length' as keyof PasswordRequirements, label: '8文字以上' },
                { key: 'uppercase' as keyof PasswordRequirements, label: '大文字を含む (A-Z)' },
                { key: 'lowercase' as keyof PasswordRequirements, label: '小文字を含む (a-z)' },
                { key: 'number' as keyof PasswordRequirements, label: '数字を含む (0-9)' }
              ].map(req => (
                <div key={req.key} className="flex items-center space-x-2">
                  <span className={`text-xs ${
                    passwordRequirements[req.key] ? 'text-green-600' : 'text-gray-400'
                  }`}>
                    {passwordRequirements[req.key] ? '✓' : '○'}
                  </span>
                  <span className={`text-xs ${
                    passwordRequirements[req.key] ? 'text-green-600 line-through' : 'text-gray-500'
                  }`}>
                    {req.label}
                  </span>
                </div>
              ))}
            </div>
          )}

          {errors.password && (
            <p className="text-red-600 text-xs mt-2 bg-red-50 p-2 rounded border border-red-200">
              <span className="font-medium">⚠️ {errors.password}</span>
            </p>
          )}
        </div>

        {/* Confirm Password */}
        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
            パスワード確認 *
          </label>
          <div className="relative">
          <input
            type="password"
            id="confirmPassword"
            value={formData.confirmPassword}
              onChange={handleConfirmPasswordChange}
              className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 ${
                errors.confirmPassword ? 'border-red-300' : 
                formData.confirmPassword && formData.password === formData.confirmPassword ? 'border-green-300' : 'border-gray-300'
            }`}
            disabled={isLoading}
              placeholder="パスワードを再入力してください"
          />
            {formData.confirmPassword && (
              <div className="absolute right-3 top-2">
                {formData.password === formData.confirmPassword ? (
                  <span className="text-green-500 text-lg">✓</span>
                ) : (
                  <span className="text-red-500 text-lg">✗</span>
                )}
              </div>
            )}
          </div>
          
          {/* パスワード一致状態の表示 */}
          {formData.confirmPassword && (
            <div className="mt-1">
              {formData.password === formData.confirmPassword ? (
                <p className="text-green-600 text-xs flex items-center">
                  <span className="mr-1">✓</span>
                  パスワードが一致しています
                </p>
              ) : (
                <p className="text-red-600 text-xs flex items-center">
                  <span className="mr-1">✗</span>
                  パスワードが一致しません
                </p>
              )}
            </div>
          )}
          
          {errors.confirmPassword && (
            <p className="text-red-600 text-xs mt-2 bg-red-50 p-2 rounded border border-red-200">
              <span className="font-medium">⚠️ {errors.confirmPassword}</span>
            </p>
          )}
        </div>

        {errors.general && (
          <div className="text-red-600 text-sm bg-red-50 p-3 rounded-md border border-red-200">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <span className="text-red-500 text-lg">⚠️</span>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  アカウント作成エラー
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{errors.general}</p>
                  {errors.general.includes('既に登録されています') && (
                    <p className="mt-2">
                      既にアカウントをお持ちの場合は、<strong>ログイン</strong>をお試しください。
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading || familyIdAvailable === false}
          className="w-full bg-pink-600 hover:bg-pink-700 disabled:bg-gray-300 text-white font-medium py-2 px-4 rounded-md transition-colors"
        >
          {isLoading ? 'アカウント作成中...' : 'アカウント作成'}
        </button>
      </form>
    </div>
  );
};

export default CustomSignUpForm; 